import { jsx } from "panel";
import isEmpty from "lodash/isEmpty";
import { TimeSeriesType } from "common/charts/visualization/line-chart/enums";
import { LineChartEl } from "common/charts/visualization/line-chart/panel";
import { SpinnerEl } from "common/components/spinner";
import { SegmentLegendHeaderEl } from "common/charts/misc/segment-legend-header/panel";
import { renderLineChartTooltip } from "common/report/insights/util/tooltip";
import { ContextMenuEl } from "common/widgets/context-menu";
import { EmptyDataEl } from "common/charts/misc/empty-data";
import { colors } from "common/util/colors";
import { canShowOtherSegment } from "common/report/insights/util";
import { renamePropertyValue } from "common/report/util/properties";
import { ChartType } from "common/report/constants";
import { getYAxisDefaultOptions } from "common/query-builder/chart-customization/utils";
import { BookmarkType } from "common/report/queries/app-api/bookmark-client";
import { getLineSeries, getOtherSegmentData } from "./util";
const LEFT_MARGIN_WITHOUT_YAXIS = 25;
export function template() {
  var _a, _b;
  const loaded = this.params.loaded && this.hasRequiredParams() && this.state.lineChartData;
  if (!loaded) {
    return /* @__PURE__ */ jsx("div", {
      sel: ".root"
    }, /* @__PURE__ */ jsx(SpinnerEl, {
      attrs: { elref: `loadingSpinner` }
    }));
  }
  if (isEmpty((_a = this.results) == null ? void 0 : _a.series)) {
    return /* @__PURE__ */ jsx("div", {
      sel: ".root"
    }, /* @__PURE__ */ jsx(EmptyDataEl, null));
  }
  const { lineChartData } = this.state;
  const seriesLookup = /* @__PURE__ */ new Map();
  lineChartData.lineChartSeries.forEach((segment) => {
    const segmentKey = segment.name;
    if (!seriesLookup.has(segmentKey)) {
      seriesLookup.set(segmentKey, []);
    }
    seriesLookup.get(segmentKey).push(segment);
  });
  const visibleChartSeries = this.segmentFilterController.getSelectedItems(0).flatMap((item) => {
    return seriesLookup.get(item.id).map((segment) => ({
      ...segment,
      color: this.segmentFilterController.getItemColor(0, item.id)
    }));
  });
  let otherSegmentData;
  if (canShowOtherSegment(this.bookmarkParams)) {
    const allSegments = Array.from(lineChartData.segmentLookup.values());
    otherSegmentData = getOtherSegmentData(allSegments, this.segmentFilterController);
    if (!otherSegmentData.isAllZero) {
      visibleChartSeries.unshift(
        getLineSeries(
          otherSegmentData.percentagesSegment,
          colors.otherSegment.primary,
          this.getProjectTimezone(),
          this.results.meta.report_sections.show[0]
        )
      );
    }
  }
  const visibleSegmentIds = visibleChartSeries.map((segment) => {
    return segment.name;
  });
  const bookmarkPrimaryYAxisOptions = this.bookmarkParams.displayOptions.primaryYAxisOptions;
  const defaultYAxisOptions = getYAxisDefaultOptions(this.bookmarkParams, BookmarkType.Insights);
  if ((bookmarkPrimaryYAxisOptions == null ? void 0 : bookmarkPrimaryYAxisOptions.min) !== void 0) {
    defaultYAxisOptions.softMin = void 0;
  }
  const bookmarkSecondaryYAxisOptions = this.bookmarkParams.displayOptions.secondaryYAxisOptions;
  const bookmarkXAxisOptions = this.bookmarkParams.displayOptions.xAxisOptions;
  return /* @__PURE__ */ jsx("div", {
    sel: ".root"
  }, !this.params.hideLegend && /* @__PURE__ */ jsx(SegmentLegendHeaderEl, {
    sel: ".segment-legend-header",
    visibleSegmentIds
  }), /* @__PURE__ */ jsx(LineChartEl, {
    sel: ".line-chart",
    ...{
      chartData: visibleChartSeries,
      chartMarginLeft: ((_b = this.params.primaryYAxisOptions) == null ? void 0 : _b.visible) === false ? LEFT_MARGIN_WITHOUT_YAXIS : void 0,
      displayAnalysisType: lineChartData.chartAnalysisType,
      displayPlotStyle: lineChartData.chartPlotStyle,
      displaySeriesType: this.bookmarkParams.displayOptions.chartType === ChartType.Column ? TimeSeriesType.Column : TimeSeriesType.Line,
      displayStackingType: lineChartData.chartStackingType,
      disableAreaRangeMouseTracking: this.hasForecastComparison(),
      ...this.params.showFirstLastTick && {
        formatTicksX: (min, max) => {
          return [min, max];
        }
      },
      formatTooltip: (_x, _y, _series, _seriesIdx, tooltipContext) => {
        const tooltipParams = this.generatePointTooltipParams(tooltipContext, otherSegmentData);
        return renderLineChartTooltip({
          ...tooltipParams,
          segments: tooltipParams.segments.map(
            (value, index) => renamePropertyValue(value, this.results.headers[index + 1])
          )
        });
      },
      ...this.formatXAxisLabel && {
        formatXAxisLabel: this.formatXAxisLabel
      },
      formatYAxisLabel: this.formatYAxisLabel,
      hideChartLines: this.params.hideChartLines,
      incompleteToDate: this.isEndDateIncomplete(),
      onHighChartClick: this.popoverCallback && this.handlePointClick,
      showAnnotations: this.params.showAnnotations,
      timeUnit: lineChartData.chartTimeUnit,
      timezone: this.getProjectTimezone().timezone,
      utcOffset: this.getProjectTimezone().utcOffset,
      defaultYAxisOptions,
      primaryYAxisOptions: {
        ...bookmarkPrimaryYAxisOptions,
        ...this.params.primaryYAxisOptions
      },
      secondaryYAxisOptions: {
        ...bookmarkSecondaryYAxisOptions,
        ...this.params.secondaryYAxisOptions
      },
      zoomType: `x`,
      xAxisOptions: {
        ...this.params.xAxisOptions,
        ...bookmarkXAxisOptions
      }
    }
  }), this.state.popoverSelection && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(ContextMenuEl, {
    attrs: {
      "menu-items": this.getPopoverAttrs()[`button-options`],
      "relative-positioning": true,
      open: true,
      position: { left: 0, top: 0 }
    },
    on: {
      action: (ev) => this.handlePopoverAction(ev),
      close: () => this.handlePopoverClose()
    },
    props: {
      positionParent: this.state.popoverPositionParent
    }
  })));
}
